import React, { FC, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import pluralize from 'pluralize';

import { SORT_ORDER } from '@evinced-private/ui-common';

import { TABLE_CONFIG } from 'src/apps/dashboard/constants/BusinessUnitsTable';
import { useBusinessUnitLabelDisplayName } from 'src/apps/dashboard/helpers/BusinessUnitLabelHelper';
import { IBusinessUnitRow } from 'src/apps/dashboard/types/BusinessUnitsTableTypes';
import { ERROR_TYPE } from 'src/apps/dashboard/types/chartErrors';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { TableSkeleton } from 'src/common/components/skeletons/table-skeleton/TableSkeleton';

import { ErrorView } from '../error-view/ErrorView';

import { getBusinessUnitsColumns } from './BusinessUnitsTableHelper';

import './BusinessUnitsTable.scss';

interface IBusinessUnitsTableProps {
	data?: IBusinessUnitRow[];
	isLoading?: boolean;
	error?: unknown;
	total?: number;
	unfilteredTotal?: number;
	isUniquenessServiceEnabled?: boolean;
}

export const BusinessUnitsTable: FC<IBusinessUnitsTableProps> = ({
	data = [],
	isLoading = false,
	error,
	total = 0,
	unfilteredTotal = 0,
	isUniquenessServiceEnabled = true
}) => {
	const businessUnitDisplayName = useBusinessUnitLabelDisplayName();
	const history = useHistory();

	const columns = useMemo(
		() => getBusinessUnitsColumns(history, isUniquenessServiceEnabled),
		[history, isUniquenessServiceEnabled]
	);

	const tableOptions = useMemo(
		() => ({
			title: pluralize(businessUnitDisplayName),
			caption: 'Table showing business units with their accessibility metrics and scores',
			dataType: TABLE_CONFIG.DATA_TYPE,
			keyField: TABLE_CONFIG.KEY_FIELD,
			remote: false,
			pagination: true,
			defaultSorted: [
				{
					dataField: 'name',
					order: SORT_ORDER.ASC
				}
			]
		}),
		[businessUnitDisplayName]
	);

	if (isLoading) {
		return (
			<div className="business-units-table">
				<TableSkeleton columns={columns.length} rowCount={7} />
			</div>
		);
	}

	if (error) {
		return <ErrorView errorType={ERROR_TYPE.FAILED} />;
	}

	if (data.length === 0 && !isLoading) {
		return <ErrorView errorType={ERROR_TYPE.NO_DATA} />;
	}

	return (
		<div className="business-units-table">
			<PlatformTable
				tableId={TABLE_CONFIG.TABLE_ID}
				tableData={data}
				totalPaginationableResults={total}
				totalCount={unfilteredTotal}
				columns={columns}
				options={tableOptions}
				isTableLoading={isLoading}
				persistTableState={false}
			/>
		</div>
	);
};
